import { useNavigate, Link } from 'react-router-dom';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { toast } from '@osago/ui';
import { Button, TextInput } from '@osago/ui';

import { loginAction } from '../actions/authActions';
import { isAccountsConfigured } from '../helpers/accountsAuth';
import { loginUser } from '../services/authService';
import { useAppDispatch } from '../store';
import { User } from '../types/models/user';

import { ymEvent } from '../hooks/useYandexMetrika';
import { YandexMetrikaEvent } from '../types/metrica/YandexMetrikaEvent';

interface LoginFormData {
  email: string;
  password: string;
}

const schema = {
  email: {
    required: 'Укажите ваш email',
    pattern: {
      value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      message: 'Неправильный формат email',
    },
  },
  password: {
    required: 'Укажите ваш пароль',
  },
};

// Старые пароли генерировал бэкенд: ровно до 10 символов, только латиница и цифры.
// После перехода человек задаёт пароль сам при восстановлении, и эта маска отрезала бы
// нормальный длинный пароль ещё до отправки. Поэтому жёсткая проверка остаётся только
// там, где вход идёт прежним путём.
const legacyPasswordRules = {
  maxLength: {
    value: 10,
    message: 'Пароль может быть не более 10 символов',
  },
  pattern: {
    value: /^[A-Za-z0-9]+$/,
    message:
      'Недопустимый символ, используйте только цифры и буквы латинского алфавита',
  },
};

const LoginForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { control, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginFormData>();

  const onLoginFormSubmit: SubmitHandler<LoginFormData> = async (data) => {
    ymEvent(YandexMetrikaEvent.LK_LOGIN_SIGNIN_SUBMIT);

    const { error, user } = await loginUser(data.email, data.password);

    if (error || !user) {
      ymEvent(YandexMetrikaEvent.LK_LOGIN_SIGNIN_ERROR);

      toast.error(error || 'Ошибка входа');
      return;
    }

    const userInfo: User = user;
    ymEvent(YandexMetrikaEvent.LK_LOGIN_SIGNIN_SUCCESS);

    dispatch(loginAction(userInfo));
    navigate('/');
  };

  return (
    <form
      onSubmit={handleSubmit(onLoginFormSubmit)}
      className="form w-full max-w-lg"
      noValidate
    >
      <header className="login-header">
        <p className="logo text-3xl">
          ОСАГО
        </p>
        <p className="font-medium">Вход в Личный кабинет</p>
      </header>

      <div className="login-field mb-8">
        <Controller
          name="email"
          control={control}
          rules={schema.email}
          defaultValue=""
          render={({ field }) => (
            <TextInput
              id="email"
              label="Электронная почта"
              type="email"
              placeholder="Ваш email"
              disabled={isSubmitting}
              hasError={!!errors.email}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
        <p className="helper helper--danger mt-1">
          {errors.email?.message}
        </p>
      </div>

      <div className="login-field mb-8">
        <Controller
          name="password"
          control={control}
          rules={isAccountsConfigured() ? schema.password : { ...schema.password, ...legacyPasswordRules }}
          defaultValue=""
          render={({ field }) => (
            <TextInput
              id="password"
              label="Пароль"
              type="password"
              placeholder="Ваш пароль"
              maxLength={isAccountsConfigured() ? undefined : 10}
              disabled={isSubmitting}
              hasError={!!errors.password}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
        <p className="helper helper--danger mt-1">
          {errors.password?.message}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-between">
        <Button
          type="submit"
          isLoading={isSubmitting}
          className="w-full sm:w-auto mb-2 sm:mb-0"
        >
          Войти
        </Button>
        <Link
          onClick={() => ymEvent(YandexMetrikaEvent.LK_LOGIN_FORGOT_CLICK)}
          to="/passreset"
          className="text-sm text-royalblue font-semibold hover:underline"
        >
          Восстановить пароль
        </Link>
      </div>
    </form>
  );
};

export default LoginForm;
